import { z } from 'zod'

import { apiClient } from '../../config/axios-client'
import type {
  CopyWorkspaceItemRequest,
  CreateWorkspaceFolderRequest,
  MoveWorkspaceItemRequest,
  RenameWorkspaceItemRequest,
  UploadWorkspaceFileChunkRequest,
  WorkspaceFileUploadResponse,
  WorkspaceItem,
} from '../../shared/models/WorkspaceItem'
import type { WorkspaceVideoPreviewMetadata } from '../../shared/models/WorkspaceVideoPreview'

interface WorkspaceItemThumbnailResponse {
  image: Blob | null
  pending: boolean
  metadata?: WorkspaceVideoPreviewMetadata
}

const previewMetadataSchema = z
  .object({
    frameWidth: z.number().int().positive().max(1024),
    frameHeight: z.number().int().positive().max(1024),
    columns: z.number().int().positive().max(4),
    rows: z.number().int().positive().max(4),
    positions: z.array(z.number().finite().min(0).max(100)).min(1).max(16),
    maximumSide: z.number().int().positive().max(1024),
  })
  .refine(
    (metadata) =>
      metadata.columns * metadata.rows >= metadata.positions.length &&
      metadata.rows === Math.ceil(metadata.positions.length / metadata.columns) &&
      Math.max(metadata.frameWidth, metadata.frameHeight) <= metadata.maximumSide,
    'Invalid video preview layout.',
  )

export const workspaceItemsApi = {
  list: (workspaceId: string, parentId: string | null) =>
    apiClient.get<WorkspaceItem[]>(`workspaces/${workspaceId}/items`, {
      params: { ParentId: parentId },
    }),

  createFolder: (workspaceId: string, data: CreateWorkspaceFolderRequest) =>
    apiClient.post<WorkspaceItem>(`workspaces/${workspaceId}/items/folders`, data),

  uploadFileChunk: ({
    workspaceId,
    uploadId,
    chunkNumber,
    expectedChunkCount,
    expectedSizeBytes,
    fileName,
    mediaType,
    parentId,
    chunk,
    allowDuplicate,
  }: UploadWorkspaceFileChunkRequest) =>
    apiClient.post<WorkspaceFileUploadResponse>(`workspaces/${workspaceId}/items/files`, chunk, {
      params: {
        UploadId: uploadId,
        ChunkNumber: chunkNumber,
        ExpectedChunkCount: expectedChunkCount,
        ExpectedSizeBytes: expectedSizeBytes,
        FileName: fileName,
        MediaType: mediaType,
        ParentId: parentId,
        allowDuplicate: allowDuplicate,
      },
      headers: { 'Content-Type': 'application/octet-stream' },
    }),

  delete: (workspaceId: string, itemId: string) =>
    apiClient.delete<unknown>(`workspaces/${workspaceId}/items/${itemId}`),

  move: (workspaceId: string, itemId: string, data: MoveWorkspaceItemRequest) =>
    apiClient.patch<unknown>(`workspaces/${workspaceId}/items/${itemId}/move-to`, data),

  copy: (
    workspaceId: string,
    itemId: string,
    data: CopyWorkspaceItemRequest,
    allowDuplicate?: boolean,
  ) =>
    apiClient.patch<unknown>(`workspaces/${workspaceId}/items/${itemId}/copy-to`, data, {
      params: {
        allowDuplicate: allowDuplicate,
      },
    }),

  rename: (workspaceId: string, itemId: string, data: RenameWorkspaceItemRequest) =>
    apiClient.patch<unknown>(`workspaces/${workspaceId}/items/${itemId}/rename`, data),

  download: (workspaceId: string, itemId: string, signal?: AbortSignal) =>
    apiClient.get<Blob>(`workspaces/${workspaceId}/items/${itemId}/download`, {
      responseType: 'blob',
      signal,
    }),

  thumbnail: async (
    workspaceId: string,
    itemId: string,
  ): Promise<WorkspaceItemThumbnailResponse> => {
    const response = await apiClient.get<Blob>(
      `workspaces/${workspaceId}/items/${itemId}/thumbnail`,
      { responseType: 'blob' },
    )
    if (response.status !== 204 && response.data.size === 0) {
      throw new Error('The thumbnail response is empty.')
    }
    return {
      image: response.status === 204 ? null : response.data,
      pending: Boolean(response.headers['retry-after']),
    }
  },

  videoPreview: async (
    workspaceId: string,
    itemId: string,
    signal?: AbortSignal,
  ): Promise<WorkspaceItemThumbnailResponse> => {
    const response = await apiClient.get<Blob>(
      `workspaces/${workspaceId}/items/${itemId}/video-preview`,
      { responseType: 'blob', signal },
    )
    const pending = Boolean(response.headers['retry-after'])
    if (response.status === 204) {
      return { image: null, pending }
    }
    if (response.data.size === 0) {
      throw new Error('The video preview response is empty.')
    }
    const metadata: unknown = JSON.parse(String(response.headers['x-video-preview-metadata']))
    return { image: response.data, pending, metadata: previewMetadataSchema.parse(metadata) }
  },

  downloadArchive: (workspaceId: string, itemId: string | null) =>
    apiClient.get<Blob>(`workspaces/${workspaceId}/items/download-archive`, {
      params: itemId === null ? undefined : { ItemId: itemId },
      responseType: 'blob',
    }),

  streamVideo: (workspaceId: string, itemId: string) =>
    apiClient.get<Blob>(`workspaces/${workspaceId}/items/${itemId}/stream`, {
      responseType: 'blob',
    }),
}
